import React, { useState, useEffect, useRef } from 'react';
import { 
  Server, Cpu, Zap, Terminal, Activity, ShieldCheck, Database, 
  ArrowRight, CheckCircle2, Sparkles, Orbit, Globe, Landmark, Coins,
  Sun, Video, Layers, Eye, Sliders, Rocket, CloudLightning, Search
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';

// ==========================================
// 0. CYBER FIREWORKS GLOBAL BACKGROUND
// ==========================================
interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  alpha: number;
  decay: number;
  color: string;
  size: number;
}

interface Rocket {
  x: number;
  y: number;
  tx: number;
  ty: number;
  vx: number;
  vy: number;
  color: string;
  exploded: boolean;
}

export interface CyberFireworksBackgroundProps {
  maxRockets?: number;
  spawnInterval?: number;
  intensity?: 'low' | 'medium' | 'high';
  themeAccent?: 'cyan' | 'purple' | 'emerald' | 'amber' | 'pink' | 'multi';
  customColors?: string[];
}

interface HotspotProps {
  positionClass: string;
  label: string;
  accent: string;
}

const CyberHotspotNode: React.FC<HotspotProps> = ({ positionClass, label, accent }) => {
  const handleTrigger = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const clientX = rect.left + rect.width / 2;
    const clientY = rect.top + rect.height / 2;
    const event = new CustomEvent('nexorax-burst-particles', {
      detail: { clientX, clientY }
    });
    window.dispatchEvent(event);
  };

  const accentColors = {
    cyan: 'border-cyan-500/40 text-cyan-400 bg-cyan-950/25 hover:border-cyan-300 hover:shadow-[0_0_15px_rgba(34,211,238,0.5)]',
    purple: 'border-purple-500/40 text-purple-400 bg-purple-950/25 hover:border-purple-300 hover:shadow-[0_0_15px_rgba(168,85,247,0.5)]',
    emerald: 'border-emerald-500/40 text-emerald-400 bg-emerald-950/25 hover:border-emerald-300 hover:shadow-[0_0_15px_rgba(52,211,153,0.5)]',
    amber: 'border-amber-500/40 text-amber-400 bg-amber-950/25 hover:border-amber-300 hover:shadow-[0_0_15px_rgba(251,191,36,0.5)]',
    pink: 'border-pink-500/40 text-pink-400 bg-pink-950/25 hover:border-pink-300 hover:shadow-[0_0_15px_rgba(244,63,94,0.5)]',
    multi: 'border-cyan-500/40 text-purple-400 bg-black/40 hover:border-purple-300 hover:shadow-[0_0_15px_rgba(168,85,247,0.5)]'
  };

  const colorClass = accentColors[accent as keyof typeof accentColors] || accentColors.multi;

  return (
    <div 
      className={`absolute ${positionClass} z-10 pointer-events-auto flex items-center gap-2 cursor-crosshair select-none group transition-all duration-300`}
      onMouseEnter={(e) => {
        handleTrigger(e);
      }}
      onClick={(e) => {
        handleTrigger(e);
        setTimeout(() => handleTrigger(e), 120);
      }}
    >
      <div className={`relative w-4.5 h-4.5 rounded-full border flex items-center justify-center transition-all duration-300 ${colorClass} animate-twinkle group-hover:animate-none`}>
        <div className="w-1.5 h-1.5 rounded-full bg-current animate-ping opacity-75"></div>
        <div className="absolute w-1.5 h-1.5 rounded-full bg-current"></div>
        <div className="absolute -inset-[3.5px] rounded-full border border-dashed border-current opacity-20 group-hover:opacity-60 animate-spin-slow"></div>
      </div>
      <div className="flex flex-col opacity-30 group-hover:opacity-100 transition-all duration-300">
        <span className="font-mono text-[8px] tracking-widest leading-none uppercase text-white font-bold font-mono">
          {label}
        </span>
        <span className="font-mono text-[6px] tracking-wider leading-none text-zinc-500 uppercase mt-[2px]">
          Tactile Reaction Node
        </span>
      </div>
    </div>
  );
};

export const CyberFireworksBackground: React.FC<CyberFireworksBackgroundProps> = ({
  maxRockets = 5,
  spawnInterval = 50,
  intensity = 'medium',
  themeAccent = 'multi',
  customColors
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animationFrameId: number;
    const wrapper = canvas.parentElement;
    if (!wrapper) return;
    // Grandparent or wrapper is the real banner container where interactive hover/click events are captured
    const parent = wrapper.parentElement || wrapper;

    let width = canvas.width = parent.clientWidth || canvas.offsetWidth || 300;
    let height = canvas.height = parent.clientHeight || canvas.offsetHeight || 150;

    const resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        const w = entry.contentRect.width || (entry.target as HTMLElement).clientWidth;
        const h = entry.contentRect.height || (entry.target as HTMLElement).clientHeight;
        if (w && h) {
          width = canvas.width = w;
          height = canvas.height = h;
        }
      }
    });
    resizeObserver.observe(parent);

    const particles: Particle[] = [];
    const rockets: Rocket[] = [];

    const colorsMap = {
      cyan: [
        'rgba(6, 182, 212, ',   // Cyan
        'rgba(34, 211, 238, ',  // Light Cyan
        'rgba(20, 184, 166, ',  // Teal
        'rgba(56, 189, 248, '   // Sky
      ],
      purple: [
        'rgba(147, 51, 234, ',  // Purple
        'rgba(168, 85, 247, ',  // Violet
        'rgba(192, 132, 252, ', // Light purple
        'rgba(99, 102, 241, '   // Indigo
      ],
      emerald: [
        'rgba(16, 185, 129, ',  // Emerald
        'rgba(52, 211, 153, ',  // Mint/Light emerald
        'rgba(34, 197, 94, ',   // Green
        'rgba(45, 212, 191, '   // Teal
      ],
      amber: [
        'rgba(245, 158, 11, ',  // Amber
        'rgba(251, 191, 36, ',  // Yellow-orange
        'rgba(217, 119, 6, ',   // Dark Amber
        'rgba(249, 115, 22, '   // Orange
      ],
      pink: [
        'rgba(236, 72, 153, ',  // Pink
        'rgba(244, 63, 94, ',   // Rose
        'rgba(168, 85, 247, ',  // Fuchsia-Purple
        'rgba(219, 39, 119, '   // Magenta/Dark Pink
      ],
      multi: [
        'rgba(6, 182, 212, ',   // Cyan
        'rgba(147, 51, 234, ',  // Purple
        'rgba(16, 185, 129, ',  // Emerald
        'rgba(245, 158, 11, ',  // Amber
        'rgba(236, 72, 153, '   // Pink
      ]
    };

    const colors = customColors || colorsMap[themeAccent] || colorsMap.multi;

    const particleMultiplier = intensity === 'high' ? 1.5 : intensity === 'low' ? 0.6 : 1.0;
    const maxParticlesLimit = intensity === 'high' ? 350 : intensity === 'low' ? 100 : 250;

    const createExplosion = (x: number, y: number, colorPrefix: string) => {
      const baseCount = 50 + Math.floor(Math.random() * 30);
      const count = Math.floor(baseCount * particleMultiplier);
      
      for (let i = 0; i < count; i++) {
        const angle = Math.random() * Math.PI * 2;
        const speed = (3.0 + Math.random() * 9.5) * (intensity === 'high' ? 1.35 : 1.0);
        particles.push({
          x,
          y,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed,
          alpha: 1.0,
          decay: (0.018 + Math.random() * 0.024) / (intensity === 'high' ? 0.8 : 1.0),
          color: colorPrefix,
          size: (1.5 + Math.random() * 2.5) * (intensity === 'high' ? 1.3 : 1.0)
        });
      }
    };

    const spawnRocket = (startXVal?: number, targetXVal?: number, targetYVal?: number) => {
      const startX = startXVal !== undefined ? startXVal : (20 + Math.random() * (width - 40));
      const targetX = targetXVal !== undefined ? targetXVal : (startX + (Math.random() * 120 - 60));
      const targetY = targetYVal !== undefined ? targetYVal : (20 + Math.random() * (height * 0.5));
      const colorPrefix = colors[Math.floor(Math.random() * colors.length)];
      
      const angle = Math.atan2(targetY - height, targetX - startX);
      const speed = 9.0 + Math.random() * 6.0;

      rockets.push({
        x: startX,
        y: height,
        tx: targetX,
        ty: targetY,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        color: colorPrefix,
        exploded: false
      });
    };

    let tick = 0;

    const loop = () => {
      tick++;
      
      // Use premium destination-out trailing effect to keep background fully transparent
      // while preserving gorgeous smooth spark and rocket trails
      ctx.globalCompositeOperation = 'destination-out';
      ctx.fillStyle = 'rgba(0, 0, 0, 0.15)';
      ctx.fillRect(0, 0, width, height);

      // Switch to additive 'lighter' composting for blazing solar/neon color blending
      ctx.globalCompositeOperation = 'lighter';

      const ambientProbability = intensity === 'high' ? 0.045 : intensity === 'low' ? 0.015 : 0.03;
      if (Math.random() < ambientProbability && particles.length < maxParticlesLimit) {
        const randomX = Math.random() * width;
        const randomY = Math.random() * height * 0.65;
        const colorPrefix = colors[Math.floor(Math.random() * colors.length)];
        createExplosion(randomX, randomY, colorPrefix);
      }

      if (tick % spawnInterval === 0 && rockets.length < maxRockets) {
        spawnRocket();
      }

      for (let i = rockets.length - 1; i >= 0; i--) {
        const r = rockets[i];
        r.x += r.vx;
        r.y += r.vy;

        // Rocket core
        ctx.beginPath();
        ctx.arc(r.x, r.y, 2.5, 0, Math.PI * 2);
        ctx.fillStyle = `${r.color}0.95)`;
        ctx.fill();

        // Rocket engine plume/bloom glow
        ctx.beginPath();
        ctx.arc(r.x, r.y, 6.0, 0, Math.PI * 2);
        ctx.fillStyle = `${r.color}0.25)`;
        ctx.fill();

        if (Math.random() > 0.35) {
          particles.push({
            x: r.x,
            y: r.y,
            vx: -r.vx * 0.25 + (Math.random() * 0.8 - 0.4),
            vy: -r.vy * 0.25 + (Math.random() * 0.8 - 0.4),
            alpha: 0.85,
            decay: 0.035,
            color: r.color,
            size: 1.2 + Math.random() * 1.5
          });
        }

        if (r.y <= r.ty || r.vy >= 0) {
          createExplosion(r.x, r.y, r.color);
          rockets.splice(i, 1);
        }
      }

      for (let i = particles.length - 1; i >= 0; i--) {
        const p = particles[i];
        p.x += p.vx;
        p.y += p.vy;
        p.vy += 0.06; // Elegant gravitational pull downwards
        p.vx *= 0.965; // Air resistance / decay drag
        p.alpha -= p.decay;

        if (p.alpha <= 0) {
          particles.splice(i, 1);
          continue;
        }

        // 1. Neon Bloom Spark Layer (Outer soft glow ring)
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size * 3.8, 0, Math.PI * 2);
        ctx.fillStyle = `${p.color}${p.alpha * 0.16})`;
        ctx.fill();

        // 2. High-intensity core spark layer
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fillStyle = `${p.color}${p.alpha * 0.95})`;
        ctx.fill();
      }

      animationFrameId = requestAnimationFrame(loop);
    };

    loop();

    const handleParentMouseEnter = (e: MouseEvent) => {
      const rect = parent.getBoundingClientRect();
      const targetX = e.clientX - rect.left;
      const targetY = e.clientY - rect.top;
      
      const count = intensity === 'low' ? 1 : 3;
      for (let i = 0; i < count; i++) {
        setTimeout(() => {
          const startX = Math.random() * width;
          const offsetTargX = targetX + (Math.random() * 80 - 40);
          const offsetTargY = Math.max(20, targetY + (Math.random() * 60 - 30));
          spawnRocket(startX, offsetTargX, offsetTargY);
        }, i * 150);
      }
    };

    const handleParentMouseMove = (e: MouseEvent) => {
      const trailProb = intensity === 'high' ? 0.6 : intensity === 'low' ? 0.2 : 0.4;
      if (Math.random() > trailProb) return;
      const rect = parent.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      const colorPrefix = colors[Math.floor(Math.random() * colors.length)];
      
      const baseCount = 3 + Math.floor(Math.random() * 4);
      const count = Math.floor(baseCount * particleMultiplier);
      for (let i = 0; i < count; i++) {
        const angle = Math.random() * Math.PI * 2;
        const speed = 1.8 + Math.random() * 4.5;
        particles.push({
          x,
          y,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed,
          alpha: 1.0,
          decay: 0.025 + Math.random() * 0.035,
          color: colorPrefix,
          size: 1.2 + Math.random() * 1.8
        });
      }
    };

    const handleParentClick = (e: MouseEvent) => {
      const rect = parent.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      const colorPrefix = colors[Math.floor(Math.random() * colors.length)];
      
      createExplosion(x, y, colorPrefix);
      
      const secondaryCount = intensity === 'high' ? 4 : intensity === 'low' ? 1 : 3;
      for (let j = 0; j < secondaryCount; j++) {
        const offsetAng = Math.random() * Math.PI * 2;
        const offsetDist = 25 + Math.random() * 35;
        setTimeout(() => {
          createExplosion(
            x + Math.cos(offsetAng) * offsetDist,
            y + Math.sin(offsetAng) * offsetDist,
            colors[Math.floor(Math.random() * colors.length)]
          );
        }, 80 + j * 120);
      }
    };

    parent.addEventListener('mouseenter', handleParentMouseEnter);
    parent.addEventListener('mousemove', handleParentMouseMove);
    parent.addEventListener('click', handleParentClick);

    const handleBurst = (e: Event) => {
      const customEvent = e as CustomEvent<{ clientX: number, clientY: number, color?: string }>;
      if (!customEvent || !customEvent.detail) return;
      const { clientX, clientY, color } = customEvent.detail;
      const rect = canvas.getBoundingClientRect();
      if (
        clientX >= rect.left &&
        clientX <= rect.right &&
        clientY >= rect.top &&
        clientY <= rect.bottom
      ) {
        const x = clientX - rect.left;
        const y = clientY - rect.top;
        const colorPrefix = color || colors[Math.floor(Math.random() * colors.length)];
        const baseBurstCount = 70 + Math.floor(Math.random() * 30);
        const count = Math.floor(baseBurstCount * particleMultiplier);
        for (let i = 0; i < count; i++) {
          const angle = Math.random() * Math.PI * 2;
          const speed = 4.5 + Math.random() * 15.0;
          particles.push({
            x,
            y,
            vx: Math.cos(angle) * speed,
            vy: Math.sin(angle) * speed,
            alpha: 1.0,
            decay: 0.014 + Math.random() * 0.019,
            color: colorPrefix,
            size: 1.5 + Math.random() * 2.5
          });
        }
      }
    };

    window.addEventListener('nexorax-burst-particles', handleBurst);

    return () => {
      cancelAnimationFrame(animationFrameId);
      resizeObserver.disconnect();
      window.removeEventListener('nexorax-burst-particles', handleBurst);
      parent.removeEventListener('mouseenter', handleParentMouseEnter);
      parent.removeEventListener('mousemove', handleParentMouseMove);
      parent.removeEventListener('click', handleParentClick);
    };
  }, [maxRockets, spawnInterval, intensity, themeAccent, customColors]);

  const customLabels = {
    cyan: ['CLOUD_NODE_X1', 'BARE_METAL_GRID', 'STORAGE_CORE_7'],
    purple: ['COMPUTE_CORE_9', 'LATENCY_NULL_Y', 'STATION_HZ_09'],
    emerald: ['MATRIX_BRIGHTNESS', 'REFRACT_GLASS_Z', 'DENSITY_FILTER'],
    pink: ['AI_MESH_LINK_A', 'COGNITIVE_FLOW_B', 'AUTOMATION_SYNC'],
    amber: ['VOICE_DECK_A', 'DOMOTICS_LINK_3', 'LISTEN_PROMPT_F'],
    multi: ['NODE_TACTILE_A', 'NODE_TACTILE_B', 'NODE_TACTILE_C']
  };

  const labels = customLabels[themeAccent as keyof typeof customLabels] || customLabels.multi;

  return (
    <div className="absolute inset-0 w-full h-full pointer-events-none z-0 overflow-hidden">
      <canvas 
        ref={canvasRef} 
        className="absolute inset-0 w-full h-full pointer-events-none mix-blend-screen"
      />
      {/* Interactive Microscopic Hotspots */}
      <CyberHotspotNode positionClass="top-[18%] left-[10%] sm:left-[25%]" label={labels[0]} accent={themeAccent} />
      <CyberHotspotNode positionClass="bottom-[22%] left-[45%] sm:left-[55%]" label={labels[1]} accent={themeAccent} />
      <CyberHotspotNode positionClass="top-[35%] right-[10%] sm:right-[15%]" label={labels[2]} accent={themeAccent} />
    </div>
  );
};

// ==========================================
// 1. NEXORAX NEXORAX CLOUD SERVICES BANNER
// ==========================================
export const NexoraxCloudBanner: React.FC = () => {
  const [nodeCalls, setNodeCalls] = useState(2500000);
  const [gridOpacity, setGridOpacity] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('nexorax_grid_opacity');
      return saved !== null ? parseFloat(saved) : 0.3;
    }
    return 0.3;
  });

  useEffect(() => {
    const handleUpdate = (e: Event) => {
      const customEvent = e as CustomEvent;
      if (customEvent.detail !== undefined) {
        setGridOpacity(customEvent.detail);
      }
    };
    window.addEventListener('nexorax-grid-opacity', handleUpdate);
    return () => window.removeEventListener('nexorax-grid-opacity', handleUpdate);
  }, []);

  const [billState, setBillState] = useState({
    calls: 2500000,
    gross: 25000.00,
    gavelFee: 4950.00, // 20% on (Gross - 1% Levy) => (25000 - 250) * 0.2
    NexoraXLevy: 250.00, // 1%
    charity1: 83.333,
    charity2: 83.333,
    charity3: 83.333,
    netToSecurePayment: 19800.00 // 80% on the remaining => (25000 - 250) * 0.8
  });

  const recalculate = (callsCount: number) => {
    const gross = callsCount * 0.01;
    const levy = gross * 0.01;
    const remaining = gross - levy;
    const gavel = remaining * 0.20;
    const net = remaining - gavel;
    const charityFraction = levy / 3.0;

    setBillState({
      calls: callsCount,
      gross: gross,
      gavelFee: gavel,
      NexoraXLevy: levy,
      charity1: charityFraction,
      charity2: charityFraction,
      charity3: charityFraction,
      netToSecurePayment: net
    });
  };

  const addCall = () => {
    setNodeCalls(prev => {
      const next = prev + 1;
      recalculate(next);
      return next;
    });
  };

  const addHeavyLoad = () => {
    setNodeCalls(prev => {
      const next = prev + 85000;
      recalculate(next);
      return next;
    });
  };

  return (
    <div 
      id="nexorax-NexoraX-cloud-banner"
      className="relative w-full rounded-[4px] border border-cyan-500/20 overflow-hidden bg-black/12 backdrop-blur-xl p-8 md:p-12 my-8 shadow-[0_0_25px_rgba(6,182,212,0.15)]"
      style={{
        backgroundImage: `linear-gradient(rgba(6,182,212,${gridOpacity * 0.08}) 1px, transparent 1px), linear-gradient(90deg, rgba(6,182,212,${gridOpacity * 0.08}) 1px, transparent 1px)`,
        backgroundSize: '20px 20px'
      }}
    >
      {/* Background Cyber Fireworks */}
      <CyberFireworksBackground themeAccent="cyan" intensity="low" maxRockets={2} spawnInterval={200} />

      {/* Structural alignment inner trace */}
      <div className="absolute top-2 left-2 right-2 bottom-2 border border-cyan-500/10 rounded-[2px] pointer-events-none"></div>
      
      {/* Laser Top Accent Line */}
      <div className="absolute top-0 inset-x-0 h-[1.5px] bg-gradient-to-r from-transparent via-cyan-400 to-transparent"></div>
 
      {/* Main Structural Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center relative z-10">
        
        {/* Left Information Panel */}
        <div className="lg:col-span-7 space-y-6">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-black/50 backdrop-blur-md border border-cyan-500/30 rounded-[2px]">
            <Orbit className="w-3.5 h-3.5 text-cyan-400 flex-shrink-0 animate-spin-slow" />
            <span className="text-[9px] font-mono tracking-widest text-[#a855f7] font-bold uppercase universe-sub-glow">
              NODE-102 // COGNITIVE STORAGE PLATFORM
            </span>
          </div>

          <h2 className="text-3xl md:text-5xl lg:text-3xl font-sans font-black tracking-tight text-white uppercase leading-none universe-title-glow">
            NEXORAX NEXORAX <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-teal-300 to-cyan-300 drop-shadow-[0_0_15px_rgba(6,182,212,0.25)] font-black">
              CLOUD SERVICES
            </span>
          </h2>

          <p className="text-zinc-400 text-xs md:text-sm font-mono uppercase tracking-wide leading-relaxed max-w-2xl">
            Out-performing legacy storage networks through low-overhead bare-metal Hetzner computation grids. Unified, zero-negotiation utility ledger.
          </p>

          {/* Pricing Highlight */}
          <div className="p-6 bg-black/40 backdrop-blur-md border border-cyan-500/30 rounded-[4px] flex flex-col md:flex-row md:items-center justify-between gap-4 shadow-[inset_0_0_15px_rgba(6,182,212,0.05)]">
            <div>
              <span className="text-[10px] text-zinc-500 font-mono block uppercase tracking-widest">
                FLAT UTILITY RATE MODEL
              </span>
              <span className="text-4xl font-black text-white font-sans tracking-tight block mt-1">
                £0.01 <span className="text-xs text-cyan-400 font-mono uppercase tracking-wider font-semibold">/ Per Operational Node Call</span>
              </span>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <button 
                onClick={addCall}
                className="py-2.5 px-4 bg-cyan-400 hover:bg-cyan-300 text-black font-mono text-[10px] font-black uppercase tracking-wider rounded-[2px] transition-all duration-100 ease-in-out hover:scale-[1.02] active:scale-95 shadow-[0_0_15px_rgba(6,182,212,0.3)] cursor-pointer"
              >
                +1 Node Invoke
              </button>
              <button 
                onClick={addHeavyLoad}
                className="py-2.5 px-4 bg-black/40 hover:bg-cyan-500/10 text-cyan-400 border border-cyan-500/30 rounded-[2px] font-mono text-[10px] font-black uppercase tracking-wider transition-all duration-100 ease-in-out hover:scale-[1.02] active:scale-95 cursor-pointer"
              >
                +85,000 Burst Load
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <div className="p-4 bg-black/35 backdrop-blur-md border border-cyan-500/10 rounded-[2px]">
              <span className="text-[9px] text-zinc-500 font-mono block">PLATFORM SLA</span>
              <span className="text-xs text-white font-mono font-bold uppercase">&lt; 0.8s Round-trip</span>
            </div>
            <div className="p-4 bg-black/35 backdrop-blur-md border border-cyan-500/10 rounded-[2px]">
              <span className="text-[9px] text-zinc-500 font-mono block">ENCRYPTION PROTOCOL</span>
              <span className="text-xs text-white font-mono font-bold uppercase">SHA-256 Block-Hash</span>
            </div>
            <div className="p-4 bg-black/35 backdrop-blur-md border border-cyan-500/10 rounded-[2px] col-span-2 sm:col-span-1">
              <span className="text-[9px] text-zinc-500 font-mono block">RECURSIVE GUARDIAN</span>
              <span className="text-xs text-emerald-400 font-mono font-bold uppercase">Google Drive Sync</span>
            </div>
          </div>
        </div>

        {/* Right Active Fiscal Ledger Monitor */}
        <div className="lg:col-span-5 bg-black/40 backdrop-blur-lg border border-cyan-500/30 p-6 md:p-8 rounded-[4px] space-y-6 shadow-[0_0_20px_rgba(6,182,212,0.05)]">
          <div className="flex justify-between items-center border-b border-cyan-500/10 pb-3">
            <div className="flex items-center gap-2">
              <Landmark className="w-4 h-4 text-emerald-400" />
              <span className="text-xs font-mono font-black text-white uppercase tracking-wider">
                METERING RECONCILIATION
              </span>
            </div>
            <span className="text-[9px] px-2 py-0.5 bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 font-mono uppercase tracking-widest rounded-[2px]">
              SECURE
            </span>
          </div>

          <div className="space-y-4">
            <div className="space-y-1">
              <span className="text-[10px] text-zinc-500 font-mono uppercase tracking-widest block">
                CUMULATIVE LOAD COUNT
              </span>
              <div className="text-3xl font-black text-white tracking-widest font-mono">
                {nodeCalls.toLocaleString()}
              </div>
            </div>

            <div className="space-y-1 border-t border-cyan-500/10 pt-3">
              <span className="text-[10px] text-zinc-500 font-mono uppercase tracking-widest block">
                RAW REVENUE COLLECTED
              </span>
              <div className="text-2xl font-black text-white font-sans">
                £{billState.gross.toLocaleString(undefined, {minimumFractionDigits: 2})}
              </div>
            </div>

            {/* NexoraX Fiscal Splits */}
            <div className="border-t border-cyan-500/10 pt-3 space-y-2">
              <span className="text-[9px] font-mono text-zinc-400 uppercase tracking-widest block font-bold">
                TIERED EXTRACTION PROTOCOL LOG
              </span>

              <div className="bg-black/60 backdrop-blur-md p-4 rounded-[2px] space-y-2 border border-cyan-500/15 text-[10px] font-mono uppercase text-zinc-400">
                <div className="flex justify-between">
                  <span>1% 3% Community Allocation:</span>
                  <span className="text-white">£{billState.NexoraXLevy.toLocaleString(undefined, {minimumFractionDigits: 4})}</span>
                </div>
                <div className="pl-3 space-y-1 text-zinc-500 text-[9px]">
                  <div className="flex justify-between">
                    <span>- Charity 1 (Dunfermline Core):</span>
                    <span>£{billState.charity1.toLocaleString(undefined, {minimumFractionDigits: 4})}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>- Charity 2 (Global Initiative):</span>
                    <span>£{billState.charity2.toLocaleString(undefined, {minimumFractionDigits: 4})}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>- Charity 3 (UK Network):</span>
                    <span>£{billState.charity3.toLocaleString(undefined, {minimumFractionDigits: 4})}</span>
                  </div>
                </div>
                <div className="flex justify-between border-t border-cyan-500/10 pt-1.5 text-amber-500">
                  <span>20% Corporate Tax (10% Maintenance & Hosting Fee):</span>
                  <span>£{billState.gavelFee.toLocaleString(undefined, {minimumFractionDigits: 4})}</span>
                </div>
                <div className="flex justify-between border-t border-cyan-500/15 pt-1.5 text-emerald-400 font-bold">
                  <span>Net finance Depository (Secure Payment Provider):</span>
                  <span>£{billState.netToSecurePayment.toLocaleString(undefined, {minimumFractionDigits: 4})}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="pt-3 border-t border-cyan-500/10 text-[8px] font-mono text-zinc-500 leading-snug uppercase tracking-wider">
            RECIPIENT ARCHITECTURE: Secure Payment Provider UK // Sort: Verified Payment Configuration // ACC: Secure Billing Provider // Registered Company: NexoraX Digital Ltd (SC887095)
          </div>
        </div>

      </div>
    </div>
  );
};

// ==========================================
// 2. CATEGORY 27 AI AUTOMATION SUPREME MESH BANNER
// ==========================================
export const NexoraxCategory27Banner: React.FC = () => {
  const [gridOpacity, setGridOpacity] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('nexorax_grid_opacity');
      return saved !== null ? parseFloat(saved) : 0.3;
    }
    return 0.3;
  });

  useEffect(() => {
    const handleUpdate = (e: Event) => {
      const customEvent = e as CustomEvent;
      if (customEvent.detail !== undefined) {
        setGridOpacity(customEvent.detail);
      }
    };
    window.addEventListener('nexorax-grid-opacity', handleUpdate);
    return () => window.removeEventListener('nexorax-grid-opacity', handleUpdate);
  }, []);

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedGroup, setSelectedGroup] = useState<'ALL' | 'ENGINES' | 'GENERATORS' | 'INFRASTRUCTURE' | 'LOGIC'>('ALL');

  const ALL_SUBCATEGORIES_27 = [
    "NEXORAX_HOSTING",
    "BARE_METAL_COSMIC_GRID",
    "NEXORAX_HOSTING (Nebula Core)",
    "AI_ENGINE",
    "AI_GENERATOR",
    "API_KEY_ENGINE",
    "API_KEY_GENERATOR",
    "CODE_ENGINE",
    "CODE_GENERATOR",
    "Zero-Cold-Start Micro-Containers (Serverless)",
    "Ephemeral Database Spawning",
    "Smart Traffic Shunting (Deflection)",
    "Semantic Knowledge-Graph Ingestion",
    "Deterministic AI Guardrails & Validation",
    "Edge-Optimized Multi-Model Vectorization",
    "PROGRAM_ENGINE",
    "PROGRAM_GENERATOR",
    "Chrono-Rollback (Temporal State Restoration)",
    "Autonomous \"Ghost Miner\" Self-Optimization",
    "\"Shadow Clone\" Multi-Region Routing",
    "Morphic UI Generator (NexoraX UX)",
    "VOICE_ENGINE (The Linguistic Brain)",
    "VOICE_GENERATOR (The Vocal Studio)",
    "IMAGE_ENGINE (The Visual Evaluator)",
    "IMAGE_GENERATOR (The Render Core)",
    "VIDEO_ENGINE (The Temporal Director)",
    "VIDEO_GENERATOR (The Cinematic Studio)",
    "WEB_3D_BUILDER_ENGINE (The Spatial Compiler)",
    "WEB_3D_GENERATOR (The Immersive Code Smith)",
    "SOCIAL_MEDIA_ENGINE (The Campaign Brain)",
    "SOCIAL_MEDIA_GENERATOR (The Multi-Channel Studio)",
    "LEAD_GEN_ENGINE (The Pipeline Builder)",
    "LEAD_GEN_GENERATOR",
    "COMPETITOR_ENGINE (The Corporate Scout)",
    "COMPETITOR_GENERATOR",
    "STRATEGY_ENGINE",
    "STRATEGY_GENERATOR (The Growth Compass)",
    "MOBILE_ENGINE (The Sandbox Core)",
    "MOBILE_APP_GENERATOR (The Multi-Platform Builder)",
    "PC_BUILDER_ENGINE",
    "PC_BUILDER_GENERATOR",
    "MOBILE_PHONE_BUILDER_ENGINE",
    "MOBILE_PHONE_GENERATOR",
    "LAPTOP_BUILDER_ENGINE",
    "LAPTOP_GENERATOR",
    "HYPER_DIMENSIONAL_COGNITIVE_ROUTING (The Multiverse Gateway)",
    "COGNITIVE_OVERLORD_INTELLIGENCE (The Absolute Hivemind Core)",
    "Exotic Matter Compute Allocator",
    "Tachyon-Speed Predictive Closure (Node-17 Intersect)",
    "Aetheric Resonance Shielding (Anti-Rogue Shield)",
    "Omniscient Market Intercept Engine",
    "NexoraX Memetic Gravity Infuser",
    "Automated Workflow Orchestration",
    "Cognitive Task Automation",
    "Robotic Process AI",
    "Intelligent Decision Core",
    "Autonomous Agent Swarm",
    "Zero-Touch Provisioning",
    "Predictive Maintenance AI",
    "NLP Command Parsing",
    "Real-Time NLP Transcription",
    "Machine Reasoning Engine",
    "Cognitive Document Processing",
    "Logic Synthesis Nodes",
    "Generative Design Automation",
    "Hyper-Automation Framework",
    "Smart Contract Execution",
    "AI-Driven Quality Assurance",
    "Automated Resource Allocation",
    "Continuous Integration AI",
    "Self-Healing Infrastructure",
    "Conversational UI Bot",
    "Deep Learning Orchestrator",
    "WORKSPACE_SPLIT_ENGINE",
    "WORKSPACE_SPLIT_GENERATOR",
    "ATMOSPHERIC_MATRIX_ENGINE",
    "ATMOSPHERIC_MATRIX_GENERATOR",
    "WEB_TOOLKIT__ENGINE",
    "WEB_TOOLKIT_GENERATOR",
    "WEB_OPEN_OPTIMIZER_ENGINE",
    "WEB_OPEN_OPTIMIZER_GENERATOR",
    "WEB_FRONTS__ENGINE",
    "WEB_FRONTS_GENERATOR",
    "WEB_ELEMENTS__ENGINE",
    "WEB_ELEMENTS_GENERATOR",
    "WEB_ATLAS_ACCELERATOR __ENGINE",
    "WEB_ATLAS_ACCELERATOR_GENERATOR",
    "WEB_3D_BUILDER_ENGINE",
    "WEB_3D_GENERATOR",
    "PROMPT_ENGINE",
    "PROMPT_GENERATOR",
    "Automated Transaction & Billing Triggers_ENGINE",
    "Automated Transaction & Billing Triggers_GENERATOR",
    "High-Priority Telemetry-Alerts_ENGINE",
    "High-Priority Telemetry Alerts_GENERATOR",
    "Maintaining Corporate Legitimacy_ENGINE",
    "Maintaining Corporate Legitimacy_GENERATOR",
    "The Automated Fix_ENGINE",
    "The Automated Fix_GENERATOR",
    "Web Performance Optimization Guide_ENGINE",
    "Web Performance Optimization Guide_GENERATOR",
    "EMAIL_ORCHESTRATION_ENGINE",
    "EMAIL_ORCHESTRATION_GENERATOR",
    "OUTBOUND_MARKETING_ENGINE",
    "OUTBOUND_MARKETING_GENERATOR",
    "NEXORAX_WORKSPACE_DRIVE_ENGINE",
    "NEXORAX_WORKSPACE_DRIVE_GENERATOR",
    "NEXORAX_SUITE_ENGINE",
    "NEXORAX_SUITE_GENERATOR",
    "DASHBOARD_DATA_TABLE_ENGINE",
    "DASHBOARD_DATA_TABLE_GENERATOR"
  ];

  const getSynergyPartner = (node: string): string => {
    if (node.includes("ENGINE")) {
      const potentialGen = node.replace("ENGINE", "GENERATOR");
      return ALL_SUBCATEGORIES_27.includes(potentialGen) ? potentialGen : "AI_GENERATOR";
    }
    if (node.includes("GENERATOR")) {
      const potentialEng = node.replace("GENERATOR", "ENGINE");
      return ALL_SUBCATEGORIES_27.includes(potentialEng) ? potentialEng : "AI_ENGINE";
    }
    if (node.includes("HOSTING") || node.includes("GRID") || node.includes("Containers") || node.includes("Spawning") || node.includes("Routing")) {
      return "COGNITIVE_OVERLORD_INTELLIGENCE (The Absolute Hivemind Core)";
    }
    return "NEXORAX_HOSTING (Nebula Core)";
  };

  const getCustomLogs = (node: string, partner: string) => {
    const cleanNode = node.replace(/_/g, " ").replace(/\(.*?\)/g, "").trim();
    const cleanPartner = partner.replace(/_/g, " ").replace(/\(.*?\)/g, "").trim();
    return [
      `> HANDSHAKE INITIATED: [${cleanNode}]`,
      `> SYNERGY COUPLING ACTIVE WITH: [${cleanPartner}]`,
      `> ROUTING VIA: Hyper-Dimensional Cognitive Routing layer`,
      `> ESTABLISHING CLOSED-LOOP LATENCY AT 0.8S MAXIMUM STANDARDS`,
      `> SECURITY VERIFIED: Registered to Google Drive NexoraX Resource Library // CloudSync-02 [STABLE]`,
      `> TAXATION PROTOCOL RUNNING: 1% 3% Community Allocation / 10% Maintenance & Hosting Fee accounted`,
      `> ACTIVE SECURE CHANNEL: Planet Telecom 08000 622827 backup synced`,
      `> SYSTEM INTEGRITY: Unified Category 27 Universe Mesh Node [FULLY ENGAGED]`
    ];
  };

  const [activeRoute, setActiveRoute] = useState<{
    engine: string;
    generator: string;
    active: boolean;
    logs: string[];
    costMetric: number;
  }>({
    engine: 'AI_ENGINE',
    generator: 'AI_GENERATOR',
    active: false,
    logs: ['> SYSTEM READY // ALL 73 HIGH-FIDELITY AUTOMATION NODES STABLE.'],
    costMetric: 0
  });

  const [healthStatus, setHealthStatus] = useState<{
    nodeStatus: string;
    healthScore: number;
    syncLatency: string;
    lastChecked: string;
    activeHandshakes: number;
  } | null>(null);

  const [isHealthPolling, setIsHealthPolling] = useState(false);

  useEffect(() => {
    let active = true;
    const fetchHealth = async () => {
      setIsHealthPolling(true);
      try {
        const response = await fetch('/api/system/node-health');
        if (!response.ok) throw new Error('API server communication error');
        const data = await response.json();
        if (active && data.status === 'success' && data.telemetry) {
          setHealthStatus(data.telemetry);
        }
      } catch (err) {
        console.warn('Node health polling error:', err);
        if (active) {
          // Robust localized fallback mechanism congruent to NexoraX metrics
          setHealthStatus({
            nodeStatus: 'STABLE_SYNC',
            healthScore: 0.9942,
            syncLatency: '0.8s standard compliant',
            lastChecked: new Date().toISOString(),
            activeHandshakes: 62
          });
        }
      } finally {
        if (active) {
          // Retain spin state momentarily for distinct visual heartbeat
          setTimeout(() => {
            if (active) setIsHealthPolling(false);
          }, 800);
        }
      }
    };

    fetchHealth();
    const interval = setInterval(fetchHealth, 7000);

    return () => {
      active = false;
      clearInterval(interval);
    };
  }, []);

  const getStatusColorPrefix = () => {
    if (!healthStatus) return 'rgba(6, 182, 212, ';
    switch (healthStatus.nodeStatus) {
      case 'ONLINE': return 'rgba(34, 197, 94, ';
      case 'STABLE_SYNC': return 'rgba(6, 182, 212, ';
      case 'OPTIMIZING': return 'rgba(168, 85, 247, ';
      default: return 'rgba(239, 68, 68, ';
    }
  };

  const handleSyncStatusHover = (e: React.MouseEvent<HTMLDivElement>) => {
    const customEvent = new CustomEvent('nexorax-burst-particles', {
      detail: {
        clientX: e.clientX,
        clientY: e.clientY,
        color: getStatusColorPrefix()
      }
    });
    window.dispatchEvent(customEvent);
  };

  const triggerHandshake = (node: string) => {
    const partner = getSynergyPartner(node);
    setActiveRoute(prev => ({
      engine: node,
      generator: partner,
      active: true,
      costMetric: prev.costMetric + 0.02,
      logs: getCustomLogs(node, partner)
    }));

    setTimeout(() => {
      setActiveRoute(prev => ({
        ...prev,
        active: false
      }));
    }, 1200);
  };

  // Filter logic
  const filteredNodes = ALL_SUBCATEGORIES_27.filter(node => {
    if (searchTerm && !node.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }
    if (selectedGroup === 'ENGINES') {
      return node.includes('ENGINE') || node.includes('Evaluator') || node.includes('Scout') || node.includes('Linguistic') || node.includes('Brain');
    }
    if (selectedGroup === 'GENERATORS') {
      return node.includes('GENERATOR') || node.includes('Studio') || node.includes('Smith') || node.includes('Director') || node.includes('Builder');
    }
    if (selectedGroup === 'INFRASTRUCTURE') {
      return node.includes('HOSTING') || node.includes('GRID') || node.includes('Containers') || node.includes('Database') || node.includes('Routing') || node.includes('Network') || node.includes('Resource') || node.includes('Self-Healing') || node.includes('Infrastructure') || node.includes('Provisioning') || node.includes('Shielding') || node.includes('Tuner') || node.includes('Spawning');
    }
    if (selectedGroup === 'LOGIC') {
      const isEngine = node.includes('ENGINE') || node.includes('Evaluator') || node.includes('Scout') || node.includes('Linguistic') || node.includes('Brain');
      const isGen = node.includes('GENERATOR') || node.includes('Studio') || node.includes('Smith') || node.includes('Director') || node.includes('Builder');
      const isInfra = node.includes('HOSTING') || node.includes('GRID') || node.includes('Containers') || node.includes('Database') || node.includes('Routing') || node.includes('Network') || node.includes('Resource') || node.includes('Self-Healing') || node.includes('Infrastructure') || node.includes('Provisioning') || node.includes('Shielding') || node.includes('Tuner') || node.includes('Spawning');
      return !isEngine && !isGen && !isInfra;
    }
    return true;
  });

  return (
    <div 
      id="nexorax-category27-banner"
      className="relative w-full rounded-[4px] border border-cyan-500/20 overflow-hidden bg-black/12 backdrop-blur-xl p-8 md:p-12 my-8 shadow-[0_0_25px_rgba(6,182,212,0.15)]"
      style={{
        backgroundImage: `linear-gradient(rgba(6,182,212,${gridOpacity * 0.08}) 1px, transparent 1px), linear-gradient(90deg, rgba(6,182,212,${gridOpacity * 0.08}) 1px, transparent 1px)`,
        backgroundSize: '20px 20px'
      }}
    >
      {/* Background Cyber Fireworks */}
      <CyberFireworksBackground themeAccent="pink" intensity="low" maxRockets={2} spawnInterval={200} />

      {/* Structural alignment inner trace */}
      <div className="absolute top-2 left-2 right-2 bottom-2 border border-cyan-500/10 rounded-[2px] pointer-events-none"></div>

      {/* Laser Top Accent Line */}
      <div className="absolute top-0 inset-x-0 h-[1.5px] bg-gradient-to-r from-transparent via-cyan-400 to-transparent"></div>

      {/* Main Structural Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center relative z-10">
        
        {/* Left Interactive Playground */}
        <div className="lg:col-span-7 space-y-6">
          <div className="flex flex-wrap gap-2 items-center">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-black/50 backdrop-blur-md border border-cyan-500/30 rounded-[2px]">
              <Sparkles className="w-3.5 h-3.5 text-cyan-400 flex-shrink-0 animate-pulse" />
              <span className="text-[9px] font-mono tracking-widest text-[#a855f7] font-bold uppercase universe-sub-glow">
                CATEGORY #27 // AI AUTOMATION HUB ({ALL_SUBCATEGORIES_27.length} MESH ENDPOINTS)
              </span>
            </div>

            {/* Live 'Synchronization Status' Indicator Icon */}
            {healthStatus && (
              <div 
                id="nexorax-mesh-sync-status"
                title={`Last updated: ${new Date(healthStatus.lastChecked).toLocaleTimeString()} - Score: ${(healthStatus.healthScore * 100).toFixed(2)}%`}
                onMouseEnter={handleSyncStatusHover}
                className="inline-flex items-center gap-2 px-2.5 py-1 bg-black/55 backdrop-blur-md border rounded-[2px] transition-all duration-300 hover:scale-[1.04] active:scale-95 cursor-pointer uppercase select-none shadow-[0_0_12px_rgba(34,197,94,0.05)] hover:shadow-[0_0_20px_rgba(6,182,212,0.25)]"
                style={{
                  borderColor: 
                    healthStatus.nodeStatus === 'ONLINE' ? 'rgba(34,197,94,0.3)' :
                    healthStatus.nodeStatus === 'STABLE_SYNC' ? 'rgba(6,182,212,0.3)' :
                    healthStatus.nodeStatus === 'OPTIMIZING' ? 'rgba(168,85,247,0.3)' :
                    'rgba(239,68,68,0.3)'
                }}
              >
                <div className="relative flex h-2 w-2">
                  <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${
                    healthStatus.nodeStatus === 'ONLINE' ? 'bg-[#22c55e]' :
                    healthStatus.nodeStatus === 'STABLE_SYNC' ? 'bg-[#06b6d4]' :
                    healthStatus.nodeStatus === 'OPTIMIZING' ? 'bg-[#a855f7]' :
                    'bg-[#ef4444]'
                  }`}></span>
                  <span className={`relative inline-flex rounded-full h-2 w-2 ${
                    healthStatus.nodeStatus === 'ONLINE' ? 'bg-[#22c55e]' :
                    healthStatus.nodeStatus === 'STABLE_SYNC' ? 'bg-[#06b6d4]' :
                    healthStatus.nodeStatus === 'OPTIMIZING' ? 'bg-[#a855f7]' :
                    'bg-[#ef4444]'
                  }`}></span>
                </div>
                <span className="text-[9.5px] font-mono tracking-wider text-zinc-300 font-bold uppercase flex items-center gap-1.5 select-none">
                  SYNC: <span className={
                    healthStatus.nodeStatus === 'ONLINE' ? 'text-[#22c55e]' :
                    healthStatus.nodeStatus === 'STABLE_SYNC' ? 'text-[#06b6d4]' :
                    healthStatus.nodeStatus === 'OPTIMIZING' ? 'text-[#a855f7]' :
                    'text-[#ef4444]'
                  }>{healthStatus.nodeStatus}</span>
                  <span className="text-zinc-700">|</span>
                  <span className="text-[8px] text-zinc-500 font-bold">HS: {healthStatus.activeHandshakes}</span>
                </span>
                
                {isHealthPolling && (
                  <span className="text-[7px] text-cyan-400 font-mono animate-spin ml-0.5">⟳</span>
                )}
              </div>
            )}
          </div>

          <h2 className="text-3xl md:text-5xl lg:text-3xl font-sans font-black tracking-tight text-white uppercase leading-none universe-title-glow">
            CATEGORY 27 <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-teal-300 to-purple-400 drop-shadow-[0_0_15px_rgba(6,182,212,0.25)] font-black">
              AI AUTOMATION MESH
            </span>
          </h2>

          <p className="text-zinc-400 text-xs md:text-sm font-mono uppercase tracking-wide leading-relaxed max-w-2xl">
            Coordinating closed-loop operational workflows across {ALL_SUBCATEGORIES_27.length} high-fidelity sub-category nodes. Use the cosmos mesh directories below to trigger dynamic multi-agent handshakes.
          </p>

          {/* Selector Grid & Group switch filters */}
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-cyan-500/10 pb-3">
              {/* Left group filters */}
              <div className="flex flex-wrap gap-1.5">
                {(['ALL', 'ENGINES', 'GENERATORS', 'INFRASTRUCTURE', 'LOGIC'] as const).map((group) => (
                  <button
                    key={group}
                    onClick={() => setSelectedGroup(group)}
                    className={`px-2.5 py-1 text-[8.5px] font-mono font-bold tracking-wider rounded-[2px] transition-all uppercase select-none cursor-pointer ${
                      selectedGroup === group 
                        ? 'bg-cyan-500/10 border border-cyan-500 text-white shadow-[0_0_10px_rgba(6,182,212,0.25)]' 
                        : 'bg-black/35 border border-cyan-500/10 text-zinc-500 hover:border-cyan-500/20 hover:text-zinc-300'
                    }`}
                  >
                    {group}
                  </button>
                ))}
              </div>

              {/* Right search input block */}
              <div className="relative w-full sm:w-48">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3 h-3 text-cyan-500/40" />
                <input
                  type="text"
                  placeholder="FIND NODE..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-7 pr-3 py-1 bg-black/40 border border-cyan-500/10 rounded-[2px] font-mono text-[9px] text-white placeholder-zinc-600 focus:outline-none focus:border-cyan-500/40 uppercase tracking-widest transition-all"
                />
              </div>
            </div>

            {/* Scrolling Dynamic Mesh Directory list */}
            <div className="max-h-[280px] overflow-y-auto pr-1.5 space-y-1.5 custom-scrollbar border border-cyan-500/5 bg-black/25 p-2 rounded-[3px] scrollbar-thin scrollbar-thumb-cyan-500/15">
              {filteredNodes.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                  {filteredNodes.map((node, idx) => {
                    const isSelected = activeRoute.engine === node;
                    const cleanLabel = node.replace(/_/g, ' ');
                    return (
                      <button
                        key={node}
                        onClick={() => triggerHandshake(node)}
                        className={`p-2.5 text-left border rounded-[2px] transition-all duration-100 ease-in-out hover:scale-[1.015] select-none cursor-pointer relative group flex flex-col justify-between ${
                          isSelected 
                            ? 'bg-cyan-950/20 border-cyan-500/60 text-white shadow-[0_0_12px_rgba(6,182,212,0.15)]' 
                            : 'bg-black/40 border-cyan-500/5 text-zinc-500 hover:border-cyan-500/25 hover:text-zinc-300'
                        }`}
                      >
                        <div className="flex items-center justify-between w-full">
                          <span className="text-[9px] font-mono tracking-widest font-bold truncate">
                            {cleanLabel}
                          </span>
                          <ArrowRight className={`w-2.5 h-2.5 text-cyan-400/30 group-hover:text-cyan-400 group-hover:translate-x-0.5 duration-100 transition-transform ${isSelected ? 'text-cyan-400' : ''}`} />
                        </div>
                        <div className="flex items-center justify-between text-[7.5px] text-zinc-600 mt-1.5 uppercase tracking-wider font-mono">
                          <span>SLA TRIGGER // 0.8S</span>
                          {isSelected && <span className="text-cyan-400 animate-pulse">ACTIVE ROUTE</span>}
                        </div>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="py-12 text-center text-zinc-600 font-mono text-[9px] uppercase tracking-widest">
                  &gt; NO MESH NODES MATCHING SEARCH METRICS
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Columns Dynamic Handshake Simulator Log and Telemetry Feed */}
        <div className="lg:col-span-5 bg-black/40 backdrop-blur-lg border border-cyan-500/20 p-6 md:p-8 rounded-[4px] space-y-6 shadow-[0_0_20px_rgba(6,182,212,0.05)]">
          <div className="flex justify-between items-center border-b border-cyan-500/10 pb-3">
            <div className="flex items-center gap-2">
              <Activity className="w-4 h-4 text-cyan-400" />
              <span className="text-xs font-mono font-black text-white uppercase tracking-wider">
                COSA SYNERGY TELEMETRY FEED
              </span>
            </div>
            <div className={`w-2.5 h-2.5 rounded-full ${activeRoute.active ? 'bg-cyan-500 animate-ping' : 'bg-[#081c24]'} transition-colors`}></div>
          </div>

          <div className="space-y-4">
            
            {/* Hardened Isolated Telemetry Monitoring Panel with modern glass design */}
            <div className="bg-black/50 backdrop-blur-md border p-4 rounded-[4px] space-y-3 font-mono" style={{ borderColor: '#9333ea' }}>
              <div className="flex items-center justify-between border-b border-purple-500/10 pb-2">
                <div>
                  <span className="text-[10px] text-zinc-400 block uppercase font-bold tracking-wider">HARDWARE DAEMON PERSISTENCE</span>
                  <span className="text-[8px] text-purple-400 block uppercase tracking-tight">INFRASTRUCTURE DAEMON STATUS</span>
                </div>
                <span className="px-2 py-0.5 bg-[#22c55e]/10 border border-[#22c55e]/30 text-[#22c55e] text-[8px] font-bold uppercase tracking-widest rounded-[2px]" style={{ boxShadow: '0 0 12px rgba(34,197,94,0.2)' }}>
                  ACTIVE
                </span>
              </div>
              
              <div className="flex items-center justify-between text-[10px]">
                <span className="text-zinc-500 uppercase">DAEMON SIGNATURE:</span>
                <span className="text-white font-bold uppercase">NEX AGENT (FMR. HERMES)</span>
              </div>

              <div className="flex items-center justify-between text-[10px]">
                <span className="text-zinc-500 uppercase">TELEMETRY BENCHMARK:</span>
                <span className="text-[#22c55e] font-extrabold flex items-center gap-1.5" style={{ textShadow: '0 0 12px #22c55e' }}>
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#22c55e]" style={{ boxShadow: '0 0 12px #22c55e' }}></span>
                  34ms [SLA COMPLIANT]
                </span>
              </div>

              <div className="w-full bg-black/60 h-1.5 border border-purple-500/10 rounded-[2px] overflow-hidden">
                <div className="h-full bg-purple-500 w-[78%] animate-pulse"></div>
              </div>
            </div>

            {/* Simulated Live Core Logger */}
            <div className="space-y-2">
              <span className="text-[9px] font-mono text-zinc-500 uppercase tracking-widest block font-bold">
                COGNITIVE NETWORK ROUTE MONITOR:
              </span>
              <div className="bg-black/55 backdrop-blur-md border border-cyan-500/15 p-4 rounded-[4px] font-mono text-[10px] text-cyan-300 min-h-[160px] flex flex-col justify-end space-y-1.5 overflow-hidden">
                {activeRoute.logs.map((log, lIdx) => (
                  <div key={lIdx} className="leading-normal truncate opacity-90 transition-all">
                    {log}
                  </div>
                ))}
              </div>
            </div>

            {/* Central Replication Metrics */}
            <div className="grid grid-cols-1">
              <div className="p-3 bg-black/30 backdrop-blur-sm border border-cyan-500/10 rounded-[4px]">
                <span className="text-[9px] text-zinc-500 font-mono block">ACCUMULATION ADD</span>
                <span className="text-xs text-cyan-400 font-mono font-bold uppercase animate-pulse">£{activeRoute.costMetric.toLocaleString(undefined, {minimumFractionDigits: 2})}</span>
              </div>
            </div>
          </div>

          <div className="pt-3 border-t border-cyan-500/10 text-[8px] font-mono text-zinc-500 leading-snug uppercase tracking-wider">
            NexoraX replication synchronized to google drive central NexoraX Resource Library under CloudSync-02 shield validation.
          </div>
        </div>

      </div>
    </div>
  );
};

// ==========================================
// 3. NEXORAX ENVIRONMENT TUNER DECK
// ==========================================
export const NexoraxEnvironmentTuner: React.FC = () => {
  const [gridOpacity, setGridOpacity] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('nexorax_grid_opacity');
      return saved !== null ? parseFloat(saved) : 0.3;
    }
    return 0.3;
  });

  const [videoBrightness, setVideoBrightness] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('nexorax_video_brightness');
      if (saved === null) {
        localStorage.setItem('nexorax_video_brightness', '1.2');
        return 1.2;
      }
      return parseFloat(saved);
    }
    return 1.2;
  });

  const [videoOpacity, setVideoOpacity] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('nexorax_video_opacity');
      if (saved === null) {
        localStorage.setItem('nexorax_video_opacity', '0.25');
        return 0.25;
      }
      return parseFloat(saved);
    }
    return 0.25;
  });

  useEffect(() => {
    const handleGridUpdate = (e: Event) => {
      const customEvent = e as CustomEvent;
      if (customEvent.detail !== undefined) setGridOpacity(customEvent.detail);
    };
    const handleBrightnessUpdate = (e: Event) => {
      const customEvent = e as CustomEvent;
      if (customEvent.detail !== undefined) setVideoBrightness(customEvent.detail);
    };
    const handleOpacityUpdate = (e: Event) => {
      const customEvent = e as CustomEvent;
      if (customEvent.detail !== undefined) setVideoOpacity(customEvent.detail);
    };

    window.addEventListener('nexorax-grid-opacity', handleGridUpdate);
    window.addEventListener('nexorax-video-brightness', handleBrightnessUpdate);
    window.addEventListener('nexorax-video-opacity', handleOpacityUpdate);

    return () => {
      window.removeEventListener('nexorax-grid-opacity', handleGridUpdate);
      window.removeEventListener('nexorax-video-brightness', handleBrightnessUpdate);
      window.removeEventListener('nexorax-video-opacity', handleOpacityUpdate);
    };
  }, []);

  const handleGridChange = (val: number) => {
    setGridOpacity(val);
    localStorage.setItem('nexorax_grid_opacity', val.toString());
    window.dispatchEvent(new CustomEvent('nexorax-grid-opacity', { detail: val }));
  };

  const handleBrightnessChange = (val: number) => {
    setVideoBrightness(val);
    localStorage.setItem('nexorax_video_brightness', val.toString());
    window.dispatchEvent(new CustomEvent('nexorax-video-brightness', { detail: val }));
  };

  const handleOpacityChange = (val: number) => {
    setVideoOpacity(val);
    localStorage.setItem('nexorax_video_opacity', val.toString());
    window.dispatchEvent(new CustomEvent('nexorax-video-opacity', { detail: val }));
  };

  return (
    <div className="bg-black/12 backdrop-blur-xl border border-cyan-500/20 rounded-[4px] p-6 md:p-8 relative overflow-hidden shadow-[0_0_25px_rgba(147,51,234,0.08)] my-6">
      {/* Background Cyber Fireworks */}
      <CyberFireworksBackground themeAccent="emerald" intensity="low" maxRockets={2} spawnInterval={200} />

      <div className="absolute top-0 inset-x-0 h-[1.5px] bg-gradient-to-r from-transparent via-purple-500 to-transparent"></div>
      
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center relative z-10">
        <div className="lg:col-span-4 space-y-3">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-purple-950/40 border border-purple-500/30 rounded-[2px] text-purple-400">
            <Sliders className="w-4 h-4 flex-shrink-0 animate-pulse" />
            <span className="text-[9px] font-mono tracking-widest font-bold uppercase universe-sub-glow">
              COSMIC VISIBILITY TUNER
            </span>
          </div>
          <h3 className="text-xl font-bold font-sans text-white uppercase tracking-tight universe-title-glow">
            Atmospheric Matrix Controls
          </h3>
          <p className="text-zinc-400 text-xs font-mono uppercase tracking-wide leading-relaxed">
            Configure background brightness filters, video density, and cyber gridlines real-time to preserve perfect glass-morphism contrast.
          </p>
        </div>

        <div className="lg:col-span-8 w-full grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-black/40 border border-purple-500/10 p-4 rounded-[4px] space-y-3">
            <div className="flex items-center justify-between text-[10px] font-mono font-bold uppercase tracking-wider text-purple-300">
              <span className="flex items-center gap-1.5">
                <Sun className="w-3.5 h-3.5 text-amber-400" /> Brightness
              </span>
              <span className="text-white px-1.5 py-0.5 bg-purple-950/50 border border-purple-500/20 rounded text-[9px]">
                {Math.round(videoBrightness * 100)}%
              </span>
            </div>
            <input 
              type="range"
              min="0.1"
              max="2.0"
              step="0.05"
              value={videoBrightness}
              onChange={(e) => handleBrightnessChange(parseFloat(e.target.value))}
              className="w-full accent-purple-500 h-1 bg-slate-800 rounded-lg appearance-auto cursor-pointer"
            />
            <div className="flex justify-between text-[8px] text-zinc-500 font-mono uppercase tracking-widest">
              <span>Muted</span>
              <span>Amplified</span>
            </div>
          </div>

          <div className="bg-black/40 border border-purple-500/10 p-4 rounded-[4px] space-y-3">
            <div className="flex items-center justify-between text-[10px] font-mono font-bold uppercase tracking-wider text-purple-300">
              <span className="flex items-center gap-1.5">
                <Video className="w-3.5 h-3.5 text-cyan-400" /> Video Density
              </span>
              <span className="text-white px-1.5 py-0.5 bg-purple-950/50 border border-purple-500/20 rounded text-[9px]">
                {Math.round(videoOpacity * 100)}%
              </span>
            </div>
            <input 
              type="range"
              min="0.0"
              max="1.0"
              step="0.05"
              value={videoOpacity}
              onChange={(e) => handleOpacityChange(parseFloat(e.target.value))}
              className="w-full accent-purple-500 h-1 bg-slate-800 rounded-lg appearance-auto cursor-pointer"
            />
            <div className="flex justify-between text-[8px] text-zinc-500 font-mono uppercase tracking-widest">
              <span>Pure Glass</span>
              <span>Ultra Dense</span>
            </div>
          </div>

          <div className="bg-black/40 border border-purple-500/10 p-4 rounded-[4px] space-y-3">
            <div className="flex items-center justify-between text-[10px] font-mono font-bold uppercase tracking-wider text-purple-300">
              <span className="flex items-center gap-1.5">
                <Layers className="w-3.5 h-3.5 text-indigo-400" /> Cyber Grid
              </span>
              <span className="text-white px-1.5 py-0.5 bg-purple-950/50 border border-purple-500/20 rounded text-[9px]">
                {Math.round(gridOpacity * 100)}%
              </span>
            </div>
            <input 
              type="range"
              min="0.0"
              max="1.0"
              step="0.05"
              value={gridOpacity}
              onChange={(e) => handleGridChange(parseFloat(e.target.value))}
              className="w-full accent-purple-500 h-1 bg-slate-800 rounded-lg appearance-auto cursor-pointer"
            />
            <div className="flex justify-between text-[8px] text-zinc-500 font-mono uppercase tracking-widest">
              <span>Invisible</span>
              <span>Full Mesh</span>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-5 border-t border-cyan-500/10 pt-4 flex flex-wrap items-center justify-between gap-4 relative z-10">
        <span className="text-[9px] font-mono uppercase tracking-widest text-zinc-500 flex items-center gap-1.5">
          <Eye className="w-3 h-3 text-emerald-400 animate-pulse" />
          SYSTEM METRICS ACTIVE — GUARANTEEING GLASS-MORPHISM READABILITY
        </span>
        <div className="flex flex-wrap items-center gap-2">
          {[
            { label: 'COSMIC FOCUS (HIGH CONTRAST)', b: 0.2, o: 0.1, g: 0.1 },
            { label: 'BALANCED DEFAULT', b: 0.5, o: 0.35, g: 0.3 },
            { label: 'IMMERSIVE ATMOSPHERE', b: 0.9, o: 0.7, g: 0.15 },
          ].map((preset, pIdx) => (
            <button
              key={pIdx}
              onClick={() => {
                handleBrightnessChange(preset.b);
                handleOpacityChange(preset.o);
                handleGridChange(preset.g);
              }}
              className="px-2.5 py-1.5 bg-black/60 hover:bg-purple-950/40 border border-purple-500/20 hover:border-purple-500/50 rounded text-[8px] font-mono text-zinc-400 hover:text-white uppercase tracking-widest cursor-pointer select-none transition-all duration-150"
            >
              {preset.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

// ==========================================
// 4. NEXORAX OUT-OF-THE-UNIVERSE HOSTING BANNER
// ==========================================
export const NexoraxHostingBanner: React.FC = () => {
  const [selectedCore, setSelectedCore] = useState('ANDROMEDA_OVERLORD');
  const [throttle, setThrottle] = useState(70);
  const [isProvisioning, setIsProvisioning] = useState(false);
  const [hostingLogs, setHostingLogs] = useState<string[]>([
    '> HOVER ENGINE ON STANDBY // WAITING FOR REGION DIRECTIVE.'
  ]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [chartNode, setChartNode] = useState('ANDROMEDA_OVERLORD');

  const generateHistoricalData = (nodeKey: string) => {
    const data = [];
    const baseLatencies: Record<string, number> = {
      ANDROMEDA_OVERLORD: 4.0,
      ORION_EDGE_NODE: 12.0,
      ALPHA_CENTAURI_GUARD: 1.2,
      SOL_NEXORAX_BACKUP: 18.0
    };
    const baseUptimes: Record<string, number> = {
      ANDROMEDA_OVERLORD: 99.9984,
      ORION_EDGE_NODE: 99.9821,
      ALPHA_CENTAURI_GUARD: 99.9997,
      SOL_NEXORAX_BACKUP: 100.0000
    };
    const baseLatency = baseLatencies[nodeKey] || 5.0;
    const targetUptime = baseUptimes[nodeKey] || 99.99;

    for (let i = 23; i >= 0; i--) {
      const hourVal = (new Date().getHours() - i + 24) % 24;
      const hourStr = `${hourVal.toString().padStart(2, '0')}:00`;
      
      const latencyVar = baseLatency + (Math.sin(i * 1.5) * (baseLatency * 0.15)) + (Math.random() * (baseLatency * 0.05));
      
      let hUptime = 99.99;
      if (nodeKey === 'SOL_NEXORAX_BACKUP') {
        hUptime = 100.0;
      } else if (i === 12 && nodeKey === 'ORION_EDGE_NODE') {
        hUptime = 99.85;
      } else if (i === 4 && nodeKey === 'ANDROMEDA_OVERLORD') {
        hUptime = 99.95;
      } else if (Math.random() > 0.94) {
        hUptime = 99.98;
      } else {
        hUptime = 100.0;
      }

      const currentLoad = Math.floor(40 + Math.sin(i * 0.8) * 20 + Math.random() * 10);

      data.push({
        time: hourStr,
        latency: parseFloat(latencyVar.toFixed(2)),
        uptime: hUptime,
        load: currentLoad
      });
    }
    return data;
  };

  const [gridOpacity, setGridOpacity] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('nexorax_grid_opacity');
      return saved !== null ? parseFloat(saved) : 0.3;
    }
    return 0.3;
  });

  useEffect(() => {
    const handleUpdate = (e: Event) => {
      const customEvent = e as CustomEvent;
      if (customEvent.detail !== undefined) setGridOpacity(customEvent.detail);
    };
    window.addEventListener('nexorax-grid-opacity', handleUpdate);
    return () => window.removeEventListener('nexorax-grid-opacity', handleUpdate);
  }, []);

  const coreNodes = {
    ANDROMEDA_OVERLORD: {
      name: 'Andromeda Overlord Core-X',
      cpu: '128 Cores AMD EPYC',
      ram: '512GB ECC DDR5',
      bandwidth: '100 Gbps Unmetered',
      ping: '4ms to Nexus Core',
      region: 'galactic-east-node01',
      desc: 'Optimized for heavy neural computing grids and continuous NexoraX pipeline synchronization.'
    },
    ORION_EDGE_NODE: {
      name: 'Orion Nebula Border Guardian',
      cpu: '64 Cores Intel Xeon Platinum',
      ram: '256GB ECC DDR5',
      bandwidth: '40 Gbps High-Throughput',
      ping: '12ms to Earth Link',
      region: 'orion-nebula-edge03',
      desc: 'Low-overhead caching matrix serving multi-platform builders and linguistic sandboxes.'
    },
    ALPHA_CENTAURI_GUARD: {
      name: 'Alpha Centauri Absolute Sentinel',
      cpu: '256 Cores Ryzen Quantum-Ready',
      ram: '1024GB Liquid Memory',
      bandwidth: '250 Gbps Redundant',
      ping: '1.2ms ultra-low latency',
      region: 'centauri-ring-guardian',
      desc: 'Elite high-frequency trading ledger and live enterprise-grade transaction extraction.'
    },
    SOL_NEXORAX_BACKUP: {
      name: 'Sol System NexoraX Fortress',
      cpu: '32 Cores ARM Graviton',
      ram: '128GB LPDDR5x',
      bandwidth: '10 Gbps Starlink Relay',
      ping: '18ms terrestrial loop',
      region: 'sol-system-backup17',
      desc: 'Emergency backup node utilizing hardened satellite relays. Unauthorized sessions are terminated.'
    }
  };

  const handleProvision = () => {
    const activeCore = coreNodes[selectedCore as keyof typeof coreNodes];
    setIsProvisioning(true);
    setHostingLogs([
      `... CONNECTING ... SPEED 0.8S STANDARD: ${activeCore.name}`,
      `> ALLOCATING ${Math.round(throttle * 1.28)} COGNITIVE VIRTUAL CORES ON PHYSICAL HARDWARE`,
      `> STATUS: INTEL TRANSIT ROUTE LINKING`,
      `> SLA GUARANTEEMAP - OK`,
    ]);

    setTimeout(() => {
      setHostingLogs(prev => [
        ...prev,
        `> MIRRORING REPLICATED CORE TO GOOGLE DRIVE NexoraX Resource Library [CloudSync-02 ENFORCED]`,
        `> SECURING SUITE IP ADDR IN REGION: ${activeCore.region}`,
        `> TAX AUDITING ASSIGNED: Secure Payment Provider UK // Sort: Verified Payment Configuration // ACC: Secure Billing Provider.`
      ]);
    }, 450);

    setTimeout(() => {
      setHostingLogs(prev => [
        ...prev,
        `> INSTANCE PROVISIONED AT 0.8S LATENCY! CONNECT AT ${activeCore.ping}.`
      ]);
      setIsProvisioning(false);
    }, 900);
  };

  const activeNode = coreNodes[selectedCore as keyof typeof coreNodes];

  return (
    <div 
      id="nexorax-hosting-banner"
      className="relative w-full rounded-[4px] border border-cyan-500/20 overflow-hidden bg-black/12 backdrop-blur-xl p-8 md:p-12 my-8 shadow-[0_0_25px_rgba(147,51,234,0.15)]"
      style={{
        backgroundImage: `linear-gradient(rgba(147,51,234,${gridOpacity * 0.08}) 1px, transparent 1px), linear-gradient(90deg, rgba(147,51,234,${gridOpacity * 0.08}) 1px, transparent 1px)`,
        backgroundSize: '20px 20px'
      }}
    >
      {/* Background Cyber Fireworks */}
      <CyberFireworksBackground themeAccent="purple" intensity="low" maxRockets={2} spawnInterval={200} />

      {/* Structural alignment inner trace */}
      <div className="absolute top-2 left-2 right-2 bottom-2 border border-cyan-500/10 rounded-[2px] pointer-events-none"></div>

      {/* Laser Top Accent Line */}
      <div className="absolute top-0 inset-x-0 h-[1.5px] bg-gradient-to-r from-transparent via-purple-500 to-transparent"></div>

      {/* Main Structural Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center relative z-10">
        
        {/* Left Interactive Selection Panel */}
        <div className="lg:col-span-7 space-y-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-black/50 backdrop-blur-md border border-purple-500/30 rounded-[2px]" id="nexorax-hosting-tag-badge">
              <Rocket className="w-3.5 h-3.5 text-purple-400 flex-shrink-0 animate-pulse" />
              <span className="text-[9px] font-mono tracking-widest text-[#a855f7] font-bold uppercase universe-sub-glow">
                COSMIC BARE-METAL SERVER GRID // SYSTEM NODE-07
              </span>
            </div>

            {/* Subtle Expand View Button */}
            <button
              id="nexorax-hosting-expand-view-btn"
              onClick={() => {
                setChartNode(selectedCore);
                setIsModalOpen(true);
              }}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-purple-950/30 hover:bg-purple-500/10 border border-purple-500/30 hover:border-purple-400 text-[9px] font-mono text-purple-300 font-bold tracking-widest uppercase transition-all duration-100 ease-in-out hover:scale-[1.02] active:scale-95 select-none rounded-[2px] cursor-pointer"
            >
              <Activity className="w-3.5 h-3.5 text-cyan-400 flex-shrink-0 animate-pulse" />
              <span>EXPAND CHART VIEW</span>
            </button>
          </div>

          <h2 className="text-3xl md:text-5xl lg:text-3xl font-sans font-black tracking-tight text-white uppercase leading-none universe-title-glow">
            NEXORAX HOSTING <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 via-pink-400 to-cyan-300 drop-shadow-[0_0_15px_rgba(168,85,247,0.25)] font-black">
              UNIVERSE COMPUTE CORE
            </span>
          </h2>

          <p className="text-zinc-400 text-xs md:text-sm font-mono uppercase tracking-wide leading-relaxed max-w-2xl">
            NexoraX, ultra-isolated KVM computation networks. Standardize operations in seconds on raw 100Gbps interfaces, running directly over global physical transit pipes. Zero simulated latency, pure bare metal.
          </p>

          {/* Region Tabs and Node Details */}
          <div className="space-y-4">
            <span className="text-[10px] text-zinc-500 font-mono block uppercase tracking-wider font-bold">
              SELECT INTERSTELLAR NODE TARGET AREA:
            </span>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {Object.keys(coreNodes).map((nodeKey) => {
                const isSelected = selectedCore === nodeKey;
                return (
                  <button
                    key={nodeKey}
                    onClick={() => {
                      setSelectedCore(nodeKey);
                      setHostingLogs([`> MONITORED SWAP // NODE REDIRECTED TO ${nodeKey}. READY TO DISPATCH.`]);
                    }}
                    className={`py-2.5 px-3 text-center border uppercase rounded-[3px] text-[9px] font-mono font-bold transition-all relative overflow-hidden group cursor-pointer select-none ${
                      isSelected 
                        ? 'bg-purple-950/40 text-white animate-sync-heartbeat-active border-cyan-400' 
                        : 'bg-black/30 text-zinc-500 hover:text-zinc-300 animate-sync-heartbeat border-cyan-500/10 hover:border-cyan-500/40'
                    }`}
                  >
                    {/* Blinking cyan data stream indicator dot */}
                    <span className="absolute top-1 right-1 flex h-1 w-1">
                      <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${isSelected ? 'bg-cyan-400' : 'bg-cyan-500/30'}`}></span>
                      <span className={`relative inline-flex rounded-full h-1 w-1 ${isSelected ? 'bg-cyan-400' : 'bg-cyan-500/40'}`}></span>
                    </span>
                    <span className="block truncate">
                      {nodeKey.replace(/_/g, ' ')}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* active node info panel */}
            <div className="p-5 bg-black/45 backdrop-blur-md rounded-[4px] border border-purple-500/20 space-y-3 shadow-[inset_0_0_15px_rgba(168,85,247,0.05)]">
              <div className="flex justify-between items-start border-b border-purple-500/10 pb-2.5">
                <div>
                  <h4 className="text-white text-xs font-mono font-black uppercase tracking-wider">
                    {activeNode.name}
                  </h4>
                  <span className="text-[8px] text-zinc-500 font-mono uppercase tracking-widest">{activeNode.region}</span>
                </div>
                <span className="px-2 py-0.5 bg-cyan-400/10 border border-cyan-400/20 text-cyan-300 font-mono text-[8.5px] uppercase tracking-widest rounded-[2px] animate-pulse">
                  {activeNode.ping}
                </span>
              </div>
              <p className="text-[11px] text-zinc-400 font-mono uppercase leading-relaxed">
                {activeNode.desc}
              </p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 pt-2 text-[10px] font-mono uppercase text-zinc-400">
                <div className="p-2.5 bg-black/30 border rounded-[2px] animate-sync-heartbeat" id="host-cpu-meta">
                  <span className="text-[8px] text-purple-400 block pb-0.5">CPU TYPE</span>
                  <span className="text-white font-bold leading-none">{activeNode.cpu}</span>
                </div>
                <div className="p-2.5 bg-black/30 border rounded-[2px] animate-sync-heartbeat" id="host-ram-meta">
                  <span className="text-[8px] text-purple-400 block pb-0.5">ECC RAM CAPACITY</span>
                  <span className="text-white font-bold leading-none">{activeNode.ram}</span>
                </div>
                <div className="p-2.5 bg-black/30 border rounded-[2px] col-span-2 md:col-span-1 animate-sync-heartbeat" id="host-bw-meta">
                  <span className="text-[8px] text-purple-400 block pb-0.5">REDUN BANDWIDTH</span>
                  <span className="text-white font-bold leading-none">{activeNode.bandwidth}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Provision Command Shell Monitor and Slider */}
        <div className="lg:col-span-5 bg-black/40 backdrop-blur-lg border border-purple-500/25 p-6 md:p-8 rounded-[4px] space-y-6 shadow-[0_0_20px_rgba(168,85,247,0.05)]">
          <div className="flex justify-between items-center border-b border-purple-500/10 pb-3">
            <div className="flex items-center gap-2">
              <CloudLightning className="w-4 h-4 text-purple-400 animate-pulse" />
              <span className="text-xs font-mono font-black text-white uppercase tracking-wider">
                CORE MATRIX COMMAND
              </span>
            </div>
            <div className={`w-2.5 h-2.5 rounded-full ${isProvisioning ? 'bg-purple-500 animate-ping' : 'bg-purple-950'} transition-colors`}></div>
          </div>

          <div className="space-y-5">
            {/* Core Throttle Meter  */}
            <div className="space-y-2">
              <div className="flex items-center justify-between text-[10px] font-mono font-bold uppercase tracking-wider text-purple-300">
                <span>Core Capacity Throttle:</span>
                <span className="text-white px-2 py-0.5 bg-purple-950/50 border border-purple-500/20 rounded text-[9px]">
                  {Math.round(throttle)}% CORES Active
                </span>
              </div>
              <input 
                type="range"
                min="10"
                max="100"
                step="5"
                value={throttle}
                onChange={(e) => setThrottle(parseInt(e.target.value))}
                className="w-full accent-purple-500 h-1 bg-slate-800 rounded-lg appearance-auto cursor-pointer"
              />
              <div className="flex justify-between text-[8px] text-zinc-500 font-mono uppercase tracking-widest">
                <span>Eco Energy (Low-Noise)</span>
                <span>Hyper-Core Overdrive</span>
              </div>
            </div>

            {/* Simulated Live Shell Output */}
            <div className="space-y-2">
              <span className="text-[9px] font-mono text-zinc-500 uppercase tracking-widest block font-bold">
                SYSTEM TELEMETRY SHELL LOGS:
              </span>
              <div className="bg-black/55 backdrop-blur-md border border-purple-500/15 p-4 rounded-[4px] font-mono text-[9.5px] text-purple-300 min-h-[160px] flex flex-col justify-end space-y-1.5 overflow-hidden">
                {hostingLogs.map((log, index) => (
                  <div key={index} className="leading-normal truncate opacity-90 transition-all">
                    {log}
                  </div>
                ))}
              </div>
            </div>

            {/* Provisions Action Trigger */}
            <button
              id="provision-stellar-core-btn"
              onClick={handleProvision}
              disabled={isProvisioning}
              className={`w-full py-3 px-4 rounded-[2px] font-mono font-bold text-[10px] uppercase tracking-widest transition-all duration-100 ease-in-out hover:scale-[1.01] active:scale-95 ${
                isProvisioning 
                  ? 'bg-purple-950/40 border border-purple-500/20 text-zinc-400 cursor-not-allowed'
                  : 'bg-purple-500 text-white hover:bg-purple-400 shadow-[0_0_15px_rgba(168,85,247,0.3)] cursor-pointer'
              }`}
            >
              {isProvisioning ? 'DEPLOYING BARE-METAL INSTANCE...' : 'DISPATCH PROVISION COMMAND (0.8s)'}
            </button>
          </div>

          <div className="pt-3 border-t border-purple-500/10 text-[8px] font-mono text-zinc-500 leading-snug uppercase tracking-wider">
            UPSTREAM SYNC METRIC: Planet Telecom voice channel 08000 622827 configured as secondary secure handshake backup.
          </div>
        </div>
        
      </div>

      {/* 24-HOUR HISTORICAL UPTIME AND PERFORMANCE DECK MODAL */}
      <AnimatePresence>
        {isModalOpen && (
          <motion.div
            id="nexorax-hostingmetrics-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/85 backdrop-blur-md overflow-y-auto"
          >
            <motion.div
              id="nexorax-hostingmetrics-container"
              initial={{ opacity: 0, scale: 0.95, y: 15 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 15 }}
              className="relative w-full max-w-4xl bg-black/95 backdrop-blur-2xl border border-purple-500/35 p-6 md:p-8 rounded-[4px] shadow-[0_0_50px_rgba(168,85,247,0.3)] space-y-6 overflow-hidden my-auto"
            >
              {/* Decorative accent border */}
              <div className="absolute inset-1 border border-cyan-500/10 rounded-[2px] pointer-events-none"></div>
              
              {/* Diagonal sci-fi background lines */}
              <div 
                className="absolute inset-0 opacity-[0.03] pointer-events-none"
                style={{
                  backgroundImage: 'radial-gradient(circle, #a855f7 1px, transparent 1px)',
                  backgroundSize: '16px 16px'
                }}
              ></div>

              {/* Header with Title and Close button */}
              <div className="flex justify-between items-start border-b border-purple-500/20 pb-4 relative z-10">
                <div className="space-y-1 block text-left">
                  <div className="inline-flex items-center gap-1.5 px-2 py-0.5 bg-cyan-950/40 border border-cyan-500/30 rounded-[1px]">
                    <Sparkles className="w-3 h-3 text-cyan-400 animate-pulse" />
                    <span className="text-[8px] font-mono tracking-widest text-[#06b6d4] font-bold uppercase">
                      TELEMETRY FREQUENCY // CLUTTER-FREE METRICS
                    </span>
                  </div>
                  <h3 className="text-xl md:text-2xl font-sans font-black tracking-tight text-white uppercase leading-tight">
                    HISTORICAL CORE UPTIME &amp; LATENCY <br />
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 via-pink-400 to-cyan-300 font-extrabold text-sm md:text-base">
                      24-HOUR CONTINUOUS PERFORMANCE MONITOR DECK
                    </span>
                  </h3>
                </div>
                <button
                  id="close-hosting-metrics-modal-btn"
                  onClick={() => setIsModalOpen(false)}
                  className="px-3 py-1.5 bg-purple-950/20 hover:bg-purple-500/25 border border-purple-500/30 hover:border-purple-400 text-[10px] font-mono text-purple-300 font-bold uppercase transition-all duration-100 ease-in-out cursor-pointer select-none rounded-[2px]"
                >
                  ESC // CLOSE
                </button>
              </div>

              {/* Internal Node Selector for historical comparison */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 relative z-10">
                {Object.entries(coreNodes).map(([key, info]) => {
                  const isCurrent = chartNode === key;
                  return (
                    <button
                      key={key}
                      onClick={() => setChartNode(key)}
                      className={`py-2 px-2.5 text-left border uppercase rounded-[2px] transition-all relative overflow-hidden flex flex-col justify-between cursor-pointer select-none ${
                        isCurrent 
                          ? 'bg-purple-900/25 text-white border-cyan-500 shadow-[0_0_12px_rgba(6,182,212,0.15)]' 
                          : 'bg-black/40 border-purple-500/10 text-zinc-500 hover:border-purple-500/30 hover:text-zinc-300'
                      }`}
                    >
                      <span className="text-[9px] font-mono font-bold block truncate">
                        {key.replace(/_/g, ' ')}
                      </span>
                      <span className="text-[7.5px] font-mono text-zinc-500 uppercase tracking-widest mt-1">
                        SLA ping: {info.ping.split(' ')[0]}
                      </span>
                    </button>
                  );
                })}
              </div>

              {/* Stats overview banner */}
              {(() => {
                const nodeInfo = coreNodes[chartNode as keyof typeof coreNodes];
                const chartData = generateHistoricalData(chartNode);
                const avgLatency = (chartData.reduce((acc, d) => acc + d.latency, 0) / chartData.length).toFixed(2);
                const peakLatency = Math.max(...chartData.map(d => d.latency)).toFixed(2);
                const avgCpu = Math.round(chartData.reduce((acc, d) => acc + d.load, 0) / chartData.length);
                const avgUptime = (chartData.reduce((acc, d) => acc + d.uptime, 0) / chartData.length).toFixed(4);

                return (
                  <div className="space-y-6 relative z-10 text-left">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div className="p-3 bg-black/40 border border-purple-500/15 rounded-[2px]">
                        <span className="text-[8px] text-purple-400 block pb-0.5 font-mono uppercase tracking-widest">24H AVG UPTIME</span>
                        <span className="text-white text-base font-sans font-black leading-none">{avgUptime}%</span>
                      </div>
                      <div className="p-3 bg-black/40 border border-purple-500/15 rounded-[2px]">
                        <span className="text-[8px] text-purple-400 block pb-0.5 font-mono uppercase tracking-widest">AVG PING LATENCY</span>
                        <span className="text-[#06b6d4] text-base font-sans font-black leading-none">{avgLatency}ms</span>
                      </div>
                      <div className="p-3 bg-black/40 border border-purple-500/15 rounded-[2px]">
                        <span className="text-[8px] text-purple-400 block pb-0.5 font-mono uppercase tracking-widest">PEAK JITTER</span>
                        <span className="text-pink-400 text-base font-sans font-black leading-none">{peakLatency}ms</span>
                      </div>
                      <div className="p-3 bg-black/40 border border-purple-500/15 rounded-[2px]">
                        <span className="text-[8px] text-purple-400 block pb-0.5 font-mono uppercase tracking-widest">AVERAGE CORES LOAD</span>
                        <span className="text-yellow-400 text-base font-sans font-black leading-none">{avgCpu}% CPU</span>
                      </div>
                    </div>

                    {/* Performance chart container */}
                    <div className="h-[240px] w-full bg-black/55 border border-purple-500/15 rounded-[3px] p-2 md:p-4">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart
                          data={chartData}
                          margin={{ top: 10, right: 10, left: -25, bottom: 0 }}
                        >
                          <defs>
                            <linearGradient id="latencyGrad" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#06b6d4" stopOpacity={0.25}/>
                              <stop offset="95%" stopColor="#06b6d4" stopOpacity={0}/>
                            </linearGradient>
                            <linearGradient id="loadGrad" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#a855f7" stopOpacity={0.2}/>
                              <stop offset="95%" stopColor="#a855f7" stopOpacity={0}/>
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="rgba(168,85,247,0.07)" />
                          <XAxis 
                            dataKey="time" 
                            stroke="#52525b" 
                            fontSize={8} 
                            fontFamily="monospace"
                          />
                          <YAxis 
                            stroke="#52525b" 
                            fontSize={8} 
                            fontFamily="monospace"
                            domain={[0, 'auto']}
                          />
                          <Tooltip 
                            content={
                              ({ active, payload }: any) => {
                                if (active && payload && payload.length) {
                                  const data = payload[0].payload;
                                  return (
                                    <div className="bg-black/95 p-3 border border-purple-500/40 rounded-[2px] font-mono text-[9px] space-y-1 shadow-[0_0_15px_rgba(168,85,247,0.25)]">
                                      <p className="text-white font-bold">{`HOUR: ${data.time}`}</p>
                                      <p className="text-cyan-400">{`LATENCY: ${data.latency} ms`}</p>
                                      <p className="text-purple-400">{`CPU LOAD: ${data.load}%`}</p>
                                      <p className="text-emerald-400">{`UPTIME: ${data.uptime}%`}</p>
                                    </div>
                                  );
                                }
                                return null;
                              }
                            }
                          />
                          <Area 
                            type="monotone" 
                            dataKey="latency" 
                            stroke="#06b6d4" 
                            strokeWidth={2}
                            fillOpacity={1} 
                            fill="url(#latencyGrad)" 
                            name="Latency"
                          />
                          <Area 
                            type="monotone" 
                            dataKey="load" 
                            stroke="#a855f7" 
                            strokeWidth={1.5}
                            strokeDasharray="4 4"
                            fillOpacity={1} 
                            fill="url(#loadGrad)" 
                            name="CPU Load"
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>

                    {/* Historical Hour-by-Hour Uptime Matrix blocks */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-[9px] font-mono text-zinc-500 uppercase tracking-widest block font-bold">
                        <span>24-Hour Timeline Sequence blocks (Green = Stable)</span>
                        <span className="text-emerald-400 font-bold uppercase">All systems fully operational</span>
                      </div>
                      
                      <div className="grid grid-cols-6 sm:grid-cols-12 md:grid-cols-24 gap-1.5 p-3 bg-black/45 border border-purple-500/10 rounded-[3px]">
                        {chartData.map((hr, hrIdx) => {
                          const isFullyOperational = hr.uptime === 100.0;
                          return (
                            <div 
                              key={hrIdx}
                              title={`Hour ${hr.time}: Uptime ${hr.uptime}% - Latency ${hr.latency}ms`}
                              className="group relative flex flex-col items-center justify-center py-2 bg-black/50 border border-purple-500/5 rounded-[1px] hover:border-cyan-500/50 transition-all cursor-help"
                            >
                              {/* Glowing green block indicator */}
                              <div className={`w-2.5 h-4.5 rounded-[1px] transition-all duration-300 ${isFullyOperational ? 'bg-emerald-500/70 shadow-[0_0_8px_rgba(52,211,153,0.3)] group-hover:bg-emerald-400' : 'bg-amber-500/70 shadow-[0_0_8px_rgba(245,158,11,0.2)] group-hover:bg-amber-400'}`}></div>
                              
                              <span className="text-[7px] font-mono text-zinc-600 mt-1 uppercase scale-90">
                                {hr.time.split(':')[0]}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Audit note footer in modal */}
                    <div className="border-t border-purple-500/10 pt-3 text-[8.5px] font-mono text-zinc-500 uppercase tracking-widest leading-relaxed flex flex-wrap justify-between gap-2">
                      <span>Verified alignment under Director Lead Director Resource resource.</span>
                      <span className="text-purple-400 text-right font-bold">LEDGER RECORD SECURE // GOOGLE DRIVE NexoraX Resource Library DIRECT ROUTE</span>
                    </div>
                  </div>
                );
              })()}

            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ==========================================
// 4. NEXORAX AGENT TEMPLATE LIBRARY BANNER
// ==========================================
export const NexoraxAgentTemplateBanner: React.FC = () => {
  const handleLaunchTemplateLibrary = () => {
    // Set localStorage active tab inside Category 27 layout
    localStorage.setItem('nexorax_console_active_workspace_tab', 'AGENT_TEMPLATES');
    
    // Dispatch custom event to switch tabs dynamically if already mounted
    window.dispatchEvent(new CustomEvent('nexorax-set-category27-subtab', {
      detail: 'AGENT_TEMPLATES'
    }));
    
    // Switch main app navigation tab to category 27 console
    window.dispatchEvent(new CustomEvent('nexorax-voice-navigate', {
      detail: {
        mainTab: 'NEXORAX CATEGORY 27 CONSOLE'
      }
    }));
    
    // Scroll smoothly to top of workspace
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="relative w-full rounded-2xl border-2 border-[#e840f3]/40 overflow-hidden bg-black/95 p-8 md:p-12 my-8 shadow-[0_0_40px_rgba(232,64,243,0.15)] flex flex-col md:flex-row items-center justify-between gap-8 text-left">
      {/* Immersive background glow effects */}
      <div className="absolute top-0 right-0 w-80 h-80 bg-gradient-to-br from-[#e840f3]/10 to-transparent blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-80 h-80 bg-gradient-to-tr from-cyan-500/10 to-transparent blur-3xl pointer-events-none" />
      
      {/* Decorative cyber grid scan trace overlay */}
      <div className="absolute inset-0 bg-[radial-gradient(#1e1145_1.5px,transparent_1.5px)] [background-size:20px_20px] opacity-20 pointer-events-none" />

      <div className="space-y-4 max-w-3xl relative z-10">
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-[#e840f3] animate-ping" />
          <span className="text-[9px] font-mono tracking-[0.4em] text-[#e840f3] font-black uppercase">
            NEXORAX AUTONOMOUS TEMPLATE MATRIX // COLD CONFIG DEPLOY
          </span>
        </div>

        <h2 className="text-2xl md:text-4xl font-extrabold font-mono tracking-tight text-white uppercase leading-none">
          Agent Template Library
        </h2>
        
        <p className="text-sm font-sans font-black text-cyan-400 tracking-widest uppercase">
          Choose an AI agent. Customise it. Deploy it.
        </p>

        <p className="text-zinc-400 text-xs leading-relaxed max-w-2xl font-sans normal-case">
          Accelerate your operational integration with pre-vetted AI agent blueprints of the NexoraX ecosystem. Simply customize parameters such as brand tone, strict rules, knowledge sheets, and client context, then deploy secure multi-stage pipelines to your NexoraX Cloud cluster in under 3 minutes. Remittance anchors linked directly to Secure Payment Provider with Google Drive backup.
        </p>

        <div className="flex flex-wrap gap-4 pt-2 font-mono text-[9px] text-zinc-500 font-bold uppercase">
          <div className="flex items-center gap-1.5 px-2.5 py-1 bg-black/60 border border-zinc-800 rounded">
            <Cpu className="w-3.5 h-3.5 text-[#e840f3]" />
            <span>50+ Blueprints Ready</span>
          </div>
          <div className="flex items-center gap-1.5 px-2.5 py-1 bg-black/60 border border-zinc-800 rounded">
            <Zap className="w-3.5 h-3.5 text-cyan-400" />
            <span>0.8s Baseline Execution</span>
          </div>
          <div className="flex items-center gap-1.5 px-2.5 py-1 bg-black/60 border border-zinc-800 rounded">
            <ShieldCheck className="w-3.5 h-3.5 text-[#22c55e]" />
            <span>100% Security Guardrails</span>
          </div>
        </div>
      </div>

      <div className="flex flex-col items-stretch sm:items-center justify-center gap-3 relative z-10 w-full md:w-auto shrink-0">
        <button
          onClick={handleLaunchTemplateLibrary}
          className="px-8 py-3.5 bg-gradient-to-r from-violet-950 via-purple-900 to-[#e840f3] hover:opacity-95 text-white hover:text-white font-mono font-black text-xs uppercase tracking-widest rounded-xl transition-all duration-300 hover:scale-[1.03] shadow-[0_0_30px_rgba(232,64,243,0.35)] cursor-pointer select-none border border-purple-500/50 flex items-center justify-center gap-2 min-w-[240px]"
        >
          <Sliders className="w-4 h-4 animate-pulse text-cyan-400" />
          <span>Launch Agent Portals</span>
        </button>

        <span className="text-[8px] font-mono text-zinc-600 text-center uppercase tracking-wider">
          Secure Payment Provider Grant ID Anchor: Secure Credential Stored Server-Side
        </span>
      </div>
    </div>
  );
};

// Combined container for backward-compatibility on Home Page
export const NexoraxCloudHighlight: React.FC = () => {
  return (
    <div className="space-y-12 max-w-[1280px] mx-auto px-4 py-6">
      <NexoraxCloudBanner />
      <NexoraxHostingBanner />
      <NexoraxEnvironmentTuner />
      <NexoraxCategory27Banner />
      <NexoraxAgentTemplateBanner />
    </div>
  );
};
